import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import PatientPersonal from "./PatientPersonal";
import PatientContact from "./PatientContact";
import StepsWizard from "../../../components/templates/StepsWizard";
import {
  createRecord,
  loadGlobalPatient,
} from "../../patientDetails/patientDetailsSlice";
import { createReference } from "../../patientReferences/patientReferencesSlice";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { isAllowedFeature } from "../../organization/organizationSlice";
import { useLoadScript } from "@react-google-maps/api";
import moment from "moment";
var flatten = require("flat");
var unflatten = require("flat").unflatten;
const libraries = ["places"];

const NewPatientWizard = (props) => {
  const { hideWizard, onShowPatientCardClicked } = props;
  const [referrerType, setReferrerType] = useState(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyDs2QkqANgCTQIcThQFCiNkxcKGPVkS_lM",
    libraries: libraries,
    language: "he",
  });
  const dispatch = useDispatch();
  let location = useLocation();
  let { branchId } = useParams();
  let history = useHistory();

  const [nextDisabled, setNextDisabled] = useState(false);
  const [values, setValues] = useState({});
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  const loadGlobalLoading = useSelector(
    (state) => state.pendingActions["patientDetails/loadGlobalPatient"]
  );
  const createLoading = useSelector(
    (state) => state.pendingActions["patientDetails/create"]
  );

  let slide2Validation = ["first_name", "surename", "gender_id"];
  if (!branchId) {
    slide2Validation.push("branch");
  }
  if (process.env.REACT_APP_CONFIG === "matav") {
    slide2Validation.push("referrer_id");
    if (referrerType === "employees") {
      slide2Validation = slide2Validation.filter(
        (item) => item !== "sub_referrer_id"
      );
      slide2Validation.push("employee_id");
    } else {
      slide2Validation = slide2Validation.filter(
        (item) => item !== "employee_id"
      );
      slide2Validation.push("sub_referrer_id");
    }
  }
  if (isAllowedFeature("accounting_tag")) {
    slide2Validation.push("branch_extension_id");
    slide2Validation.push("subproject_id");
  }

  const steps = [
    {
      title: null,
      description: null,
      headline: null,
      subtitle: <FormattedMessage id="app.patients.personalSubtitle" />,
      required: slide2Validation,
      validation: slide2Validation,
      comp: (
        <PatientPersonal
          values={values}
          branchId={branchId}
          setReferrerType={setReferrerType}
          referrerType={referrerType}
        />
      ),
    },
    {
      title: null,
      description: null,
      subtitle: <FormattedMessage id="app.patients.addressSubtitle" />,
      required: [],
      validation: ["contact_info.phone_number", "contact_info.mobile_number"],
      comp: <PatientContact />,
    },
  ];

  const hide = () => {
    setNextDisabled(true);
    setValues({});
    hideWizard();
  };

  const setNext = (val) => {
    setNextDisabled(val);
  };

  const onFinish = async (formValues) => {
    try {
      const deepValues = getDeepValues(formValues);
      const geo = await fetchGeoDetails(deepValues.address);
      const patientParams = createPatientParams(deepValues, geo);
      const action = await dispatch(createRecord(patientParams));

      if (action.error) throw new Error("Error creating patient record");

      const patientId = action.payload.data.id;
      await handleMatavSpecificLogic(formValues, patientId);
      navigateAfterAction(patientId);
    } catch (error) {
      console.error(error);
      hide();
    }
  };

  const getDeepValues = (formValues) => {
    const deepValues = unflatten(formValues, { safe: true });
    if (values["birthday"]) deepValues["birthday"] = values["birthday"];
    return deepValues;
  };

  const fetchGeoDetails = async (addressDetails) => {
    if (addressDetails && addressDetails.city) {
      const address = `${addressDetails.city}, ${addressDetails.street} ${addressDetails.house_number}`;
      const predictions = await getNewAddress(address);
      return await getGeo(predictions["predictions"]);
    }
    return {};
  };

  const createPatientParams = (deepValues, geo) => {
    const local_id_number =
      deepValues.local_id_number === ""
        ? null
        : deepValues.local_id_number?.padStart(9, "0");

    return {
      patient: { ...deepValues, local_id_number, geo, status_id: 1 },
      branchId: location.pathname.includes("admin") ? values.branch : branchId,
    };
  };

  const handleMatavSpecificLogic = async (formValues, patientId) => {
    if (process.env.REACT_APP_CONFIG === "matav") {
      const params = {
        reference: {
          reference_time: moment().format(),
          referrer_id: formValues.referrer_id,
          sub_referrer_id: formValues.sub_referrer_id,
          employee_id: formValues.employee_id,
        },
        branchId: location.pathname.includes("admin")
          ? values.branch
          : branchId,
        patientId: patientId,
        referenceId: null,
      };
      const referenceAction = await dispatch(createReference(params));
      if (referenceAction.error) {
        throw new Error("Error creating MATaV reference");
      }
    }
  };

  const navigateAfterAction = (patientId) => {
    const isAdmin = location.pathname.includes("admin");
    const route = isAdmin
      ? "/admin/patients/"
      : "/branches/" + branchId + "/patients/";
    hide();
    history.push(route + patientId);
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const getNewAddress = async (address) => {
    const places =
      isLoaded && new window.google.maps.places.AutocompleteService();
    const predictions = await places.getPlacePredictions({
      ...props.searchOptions,
      input: address,
    });
    return predictions;
  };

  const getGeo = async (predictions) => {
    if (predictions && predictions.length > 0) {
      const results = await geocodeByAddress(predictions[0].description);
      const latLng = await getLatLng(results[0]);
      const geo = {
        longtitude: latLng.lng,
        latitude: latLng.lat,
        address_line: predictions[0].description,
      };
      return geo;
    }
  };

  const changeValues = (val) => {
    setValues(val);
  };
  const onValuesChange = async (changedValues, allValues) => {
    const newValues = unflatten(allValues);
    setValues({ ...values, ...newValues });
    if (changedValues.local_id_number) {
      if (isId(changedValues.local_id_number)) {
        const filter = {
          local_id_number: changedValues.local_id_number?.padStart(9, "0"),
        };
        const params = {
          scopePrefix,
          filter: {
            ...filter,
          },
        };
        const action = await dispatch(loadGlobalPatient(params));
        if (action.payload && action.payload.id) {
          const flattenRecord = flatten(action.payload, { safe: true });
          onShowPatientCardClicked(flattenRecord, false);
          hideWizard();
        }
      }
    }
  };

  const isId = (value) => {
    let strId = String(value).trim();
    if (strId.length > 9 || strId.length < 8) {
      return false;
    }
    if (strId.length < 9) {
      while (strId.length < 9) strId = "0" + strId;
    }
    let counter = 0,
      rawVal,
      actualVal;
    for (let i = 0; i < strId.length; i++) {
      rawVal = Number(strId[i]) * ((i % 2) + 1);
      actualVal = rawVal > 9 ? rawVal - 9 : rawVal;
      counter += actualVal;
    }
    if (counter % 10 === 0) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <StepsWizard
      steps={steps}
      setNext={setNext}
      changeValues={changeValues}
      values={values}
      onCancel={hide}
      onValuesChange={onValuesChange}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onDecline={hide}
      saveLoading={createLoading}
      createText={<FormattedMessage id="app.patients.createNewPatient" />}
      nextDisabled={nextDisabled || loadGlobalLoading}
    />
  );
};

export default NewPatientWizard;
